import { useTranslation } from 'react-i18next';
import EmptyState from 'components/EmptyState';
import { cn } from 'lib/utils';
import type { ImportChange } from 'api/technicianImportContract';
import { importCellValue, importFieldLabel, MULTI_VALUE_FIELDS } from '../importLabels';
import { tableRow } from './importTable';

const split = (value: string | null): string[] => (value ? value.split(', ') : []);

const struck = 'text-zinc-500 line-through dark:text-zinc-400';

/** CA9 — per technician, each changed field as before → after, multi-values as + / −. */
const ImportChangesPanel = ({ changes }: { changes: ImportChange[] }) => {
  const { t } = useTranslation();
  if (changes.length === 0) return <EmptyState title={t('No change in this file')} />;

  return (
    <div className="overflow-clip rounded-xl border">
      {changes.map((change) => (
        <div key={change.row}>
          <div className="flex items-center gap-2 border-b bg-muted px-3.5 py-2 text-[13px]">
            <span className="font-semibold">{change.name}</span>
            <span className="text-foreground/80">{t('· row {{row}}', { row: change.row })}</span>
          </div>
          {/* CA9: « Actif » first, then the template order */}
          {[...change.fields].sort((a, b) => Number(b.field === 'is_active') - Number(a.field === 'is_active')).map((field) => {
            const from = importCellValue(t, field.field, field.from);
            const to = importCellValue(t, field.field, field.to);
            const multi = MULTI_VALUE_FIELDS.has(field.field);
            const added = split(field.to).filter((code) => !split(field.from).includes(code));
            const removed = split(field.from).filter((code) => !split(field.to).includes(code));
            return (
              <div key={field.field} className={cn(tableRow, 'grid-cols-[200px_1fr]')}>
                <span className="text-muted-foreground">{importFieldLabel(t, field.field)}</span>
                <span className="flex flex-wrap items-center gap-2">
                  <span className="sr-only">{t('Before: {{from}} / After: {{to}}', { from, to })}</span>
                  {multi && !field.erased ? (
                    <span aria-hidden="true" className="flex flex-wrap items-center gap-1.5">
                      {added.map((code) => (
                        <span key={`+${code}`} className="rounded-md bg-emerald-50 px-1.5 py-0.5 text-[12.5px] font-semibold text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300">+ {code}</span>
                      ))}
                      {removed.map((code) => (
                        <span key={`-${code}`} className="rounded-md bg-rose-50 px-1.5 py-0.5 text-[12.5px] font-semibold text-rose-800 dark:bg-rose-950/50 dark:text-rose-300">− {code}</span>
                      ))}
                    </span>
                  ) : (
                    <span aria-hidden="true" className="flex items-center gap-2">
                      <span className={struck}>{from}</span>
                      <span className="text-muted-foreground">→</span>
                      <span className="font-semibold">{to}</span>
                    </span>
                  )}
                  {field.erased && (
                    <span className="rounded-md bg-amber-50 px-1.5 py-0.5 text-[11.5px] font-semibold text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
                      {t('erased')}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default ImportChangesPanel;
